import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LiveInsight, SuggestedQuestion, TranscriptSegment, QuestionAction } from '../../../types/live';
import { DeckFile, PageCounts, deckFileOf, deckGlobalIndex, slideRefToPosition } from '../../../types/deck';
import { prefersReducedMotion, scrollContainerToBottom, useNearBottom } from './scrollHelpers';

export type AssistantTab = 'context' | 'transcript' | 'ask';

const TABS: AssistantTab[] = ['context', 'transcript', 'ask'];

// New arrivals keep a visual accent for this long, then decay in place; the
// decay is per-item and never reorders the list. Items older than STALE_MS
// carry an age badge so out-of-date guidance is visibly out of date.
const ACCENT_MS = 10_000;
const STALE_MS = 4 * 60_000;
// Cards shown in the ON THIS SLIDE zone before the "Show N more" disclosure.
const ON_SLIDE_PREVIEW = 3;

// A unified Context entry: insights and suggested questions rank together in
// one stream instead of living in separate content-type tabs.
type ContextItem =
  | { kind: 'insight'; id: string; insight: LiveInsight }
  | { kind: 'question'; id: string; question: SuggestedQuestion };

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AssistantPanelProps {
  segments: TranscriptSegment[];
  // Live not-yet-final mic hypothesis, shown as a muted trailing line for
  // instant feedback. Empty when there is no pending partial.
  interimTranscript?: string;
  insights: LiveInsight[];
  questions: SuggestedQuestion[];
  // The slide currently on stage, used to surface "this slide" groups first.
  activeSlideRef?: string | null;
  // Ordered deck, used to label slide groups by their deck position + filename.
  deckFiles: DeckFile[];
  // Per-file page counts (lifted from the stage's PDF loaders) so slide labels
  // use the same global "Slide N" numbering as the stage counter. Counts may
  // still be loading; labels fall back gracefully until they resolve.
  pageCounts?: PageCounts;
  chatMessages: ChatMessage[];
  chatStreaming: boolean;
  canPaste: boolean;
  // Manual regeneration of the Context stream. The button only shows while the
  // meeting is live (canRefresh); refreshing drives the busy state.
  canRefresh?: boolean;
  refreshing?: boolean;
  // True while the initial insights/questions fetch is in flight — the Context
  // tab shows skeleton rows instead of a premature empty message.
  contextLoading?: boolean;
  // Whether the session is live; drives the pre-live warm-up message when the
  // Context stream is empty.
  isLive?: boolean;
  onRefreshInsights?: () => void;
  onPasteTranscript: (text: string) => Promise<boolean>;
  onClearTranscript: () => Promise<boolean>;
  onSendChat: (query: string) => void;
  // May reject: question cards keep the activated action pending and surface
  // the failure inline next to the restored control.
  onActQuestion: (question: SuggestedQuestion, action: QuestionAction) => void | Promise<void>;
}

// Count how many items in `list` are not yet in the `seen` id set.
const unseenCount = (list: { id: string }[], seen: Set<string>): number =>
  list.reduce((n, item) => (seen.has(item.id) ? n : n + 1), 0);

// Local HH:MM for a segment timestamp; empty string when unparseable so the
// timestamp element is simply omitted rather than showing "Invalid Date".
const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Coarse relative age for the stale badge ("5m ago" / "1h ago").
const ageLabel = (ms: number): string => {
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
};

// Tabbed assistant panel. Context unifies insights and suggested questions
// into one slide-relevance-ordered stream; Transcript shows shared content;
// Advisor is the per-user chat. The inactive Context tab shows an unread
// "N new" badge so live arrivals are noticed without switching tabs.
const AssistantPanel: React.FC<AssistantPanelProps> = ({
  segments,
  interimTranscript,
  insights,
  questions,
  activeSlideRef,
  deckFiles,
  pageCounts,
  chatMessages,
  chatStreaming,
  canPaste,
  canRefresh,
  refreshing,
  contextLoading,
  isLive,
  onRefreshInsights,
  onPasteTranscript,
  onClearTranscript,
  onSendChat,
  onActQuestion,
}) => {
  const [tab, setTab] = useState<AssistantTab>('context');
  const [chatInput, setChatInput] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasting, setPasting] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Per-question in-flight action and last failure. Only the activated control
  // goes busy (label change + disable); on failure the control is restored and
  // the error shows inline next to it rather than as a toast.
  const [actPending, setActPending] = useState<Record<string, QuestionAction>>({});
  const [actErrors, setActErrors] = useState<Record<string, string>>({});

  // Ids the user has already seen in Context. Everything currently present is
  // considered seen on mount so we only badge genuinely new items.
  const [seenContext, setSeenContext] = useState<Set<string>>(
    () => new Set([...insights, ...questions].map((i) => i.id))
  );

  const [announcement, setAnnouncement] = useState('');

  // Which collapsed history groups the user has expanded, keyed by
  // "tab:slide_ref", persisted for the meeting session so browsing a past
  // slide's items survives slide changes and re-renders. The on-stage module
  // and the whole-deck disclosure track their own keys here too.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const toggleGroup = useCallback((key: string, open: boolean) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const contextScrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<AssistantTab, HTMLButtonElement | null>>({
    context: null, transcript: null, ask: null,
  });

  const transcriptNearBottom = useNearBottom(transcriptScrollRef);
  const chatNearBottom = useNearBottom(chatScrollRef);

  const newContext = useMemo(
    () => unseenCount(insights, seenContext) + unseenCount(questions, seenContext),
    [insights, questions, seenContext]
  );

  // Mark Context items as seen once the user is looking at them.
  useEffect(() => {
    if (tab === 'context' && newContext > 0) {
      setSeenContext(new Set([...insights, ...questions].map((i) => i.id)));
    }
  }, [tab, insights, questions, newContext]);

  // Announce new arrivals politely for screen-reader users on other tabs
  // (once per batch commit, not per SSE event).
  useEffect(() => {
    if (tab !== 'context' && newContext > 0) {
      setAnnouncement(`${newContext} new item${newContext === 1 ? '' : 's'} in Intelligence`);
    }
  }, [newContext, tab]);

  // ---- Freshness clock ----
  // First-seen stamps drive the "new" accent (10s decay) and, for questions
  // (which carry no created_at), the stale age. Items present on mount get a
  // zero stamp so restored history never flashes as new.
  const [now, setNow] = useState(() => Date.now());
  const firstSeen = useRef(new Map<string, number>());
  const seededRef = useRef(false);
  const scrolledAwayRef = useRef(false);
  const [pendingNew, setPendingNew] = useState(0);
  useEffect(() => {
    const t = Date.now();
    let fresh = 0;
    for (const item of [...insights, ...questions]) {
      if (!firstSeen.current.has(item.id)) {
        firstSeen.current.set(item.id, seededRef.current ? t : 0);
        if (seededRef.current) fresh += 1;
      }
    }
    seededRef.current = true;
    if (fresh > 0) {
      setNow(t);
      // New items landing while the reader is scrolled away feed the jump pill
      // instead of reflowing the list under them.
      if (scrolledAwayRef.current) setPendingNew((n) => n + fresh);
    }
  }, [insights, questions]);

  // Re-render exactly when the next accent expires so the decay happens
  // without polling; a slow 30s tick keeps the stale badges current.
  useEffect(() => {
    const expiries = Array.from(firstSeen.current.values())
      .filter((s) => s > 0)
      .map((s) => s + ACCENT_MS)
      .filter((e) => e > now);
    if (expiries.length === 0) return;
    const t = setTimeout(() => setNow(Date.now()), Math.min(...expiries) - now + 20);
    return () => clearTimeout(t);
  }, [now, insights, questions]);
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(iv);
  }, []);

  const onContextScroll = useCallback(() => {
    const el = contextScrollRef.current;
    if (!el) return;
    scrolledAwayRef.current = el.scrollTop > 40;
    if (!scrolledAwayRef.current) setPendingNew(0);
  }, []);

  const jumpToTop = useCallback(() => {
    contextScrollRef.current?.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    setPendingNew(0);
  }, []);

  // Auto-follow only when the reader is already at the bottom, so scrolling up
  // to reread earlier content is never interrupted by new arrivals.
  useEffect(() => {
    if (transcriptNearBottom) scrollContainerToBottom(transcriptScrollRef.current);
  }, [segments, transcriptNearBottom]);
  useEffect(() => {
    if (chatNearBottom) scrollContainerToBottom(chatScrollRef.current);
  }, [chatMessages, chatStreaming, chatNearBottom]);

  const submitChat = () => {
    const q = chatInput.trim();
    if (!q || chatStreaming) return;
    setChatInput('');
    onSendChat(q);
  };

  // Run a question action keeping only that card's activated control pending.
  // On failure the control is restored and the error appears adjacent to it.
  const runAction = useCallback(
    async (question: SuggestedQuestion, action: QuestionAction): Promise<boolean> => {
      setActPending((prev) => ({ ...prev, [question.id]: action }));
      setActErrors(({ [question.id]: _drop, ...rest }) => rest);
      try {
        await onActQuestion(question, action);
        return true;
      } catch (err) {
        setActErrors((prev) => ({
          ...prev,
          [question.id]: err instanceof Error ? err.message : 'Action failed. Try again.',
        }));
        return false;
      } finally {
        setActPending(({ [question.id]: _drop, ...rest }) => rest);
      }
    },
    [onActQuestion]
  );

  // The Advisor handoff only happens when the ask action actually recorded —
  // a failed action restores the card in place instead of switching tabs.
  const askFromQuestion = async (question: SuggestedQuestion) => {
    const ok = await runAction(question, 'ask');
    if (!ok) return;
    setTab('ask');
    onSendChat(question.text);
  };

  // Human label for a slide group: "Slide N · filename · p.P" where N is the
  // same 1-based global deck index the stage counter shows ("Slide N of T").
  // While page counts are still loading (or the file is off-deck) the global
  // index can't be resolved, so the label falls back to filename + page only.
  const slideLabel = useCallback(
    (slideRef: string): string => {
      const pos = slideRefToPosition(slideRef);
      if (!pos) return slideRef;
      const file = deckFileOf(deckFiles, pos.fileId);
      const name = file?.filename || 'Presentation';
      const globalIndex = deckGlobalIndex(deckFiles, pageCounts ?? {}, pos);
      const nth = globalIndex != null ? `Slide ${globalIndex} · ` : '';
      return `${nth}${name} · p.${pos.page}`;
    },
    [deckFiles, pageCounts]
  );

  // Order slide groups by deck position (unknown files last), so the history
  // reads top-to-bottom like the deck.
  const slideOrder = useCallback(
    (slideRef: string): number => {
      const pos = slideRefToPosition(slideRef);
      if (!pos) return Number.MAX_SAFE_INTEGER;
      const fileIndex = deckFiles.findIndex((f) => f.fileId === pos.fileId);
      return (fileIndex < 0 ? deckFiles.length : fileIndex) * 100000 + pos.page;
    },
    [deckFiles]
  );

  // Sort a mixed insight/question list newest-arrival-first. Unstamped items
  // (arrived this render, stamped in the effect above) count as newest;
  // zero-stamped items (restored on load) keep their stored order at the tail.
  const toItems = useCallback((ins: LiveInsight[], qs: SuggestedQuestion[]): ContextItem[] => {
    const items: ContextItem[] = [
      ...ins.map((i) => ({ kind: 'insight' as const, id: i.id, insight: i })),
      ...qs.map((q) => ({ kind: 'question' as const, id: q.id, question: q })),
    ];
    const stamp = (id: string) => firstSeen.current.get(id) ?? Number.MAX_SAFE_INTEGER;
    return items.sort((a, b) => stamp(b.id) - stamp(a.id));
  }, []);

  // Pack (whole-deck) insights, unassigned "General" items, and the per-slide
  // stream grouped by slide_ref.
  const packInsights = useMemo(() => insights.filter((i) => i.category === 'pack'), [insights]);
  const generalItems = useMemo(
    () =>
      toItems(
        insights.filter((i) => i.category !== 'pack' && !i.slide_ref),
        questions.filter((q) => !q.slide_ref)
      ),
    [insights, questions, toItems]
  );
  const slideGroups = useMemo(() => {
    const byRef = new Map<string, { ins: LiveInsight[]; qs: SuggestedQuestion[] }>();
    const bucket = (key: string) => byRef.get(key) ?? byRef.set(key, { ins: [], qs: [] }).get(key)!;
    for (const i of insights) {
      if (i.category === 'pack' || !i.slide_ref) continue;
      bucket(i.slide_ref).ins.push(i);
    }
    for (const q of questions) {
      if (!q.slide_ref) continue;
      bucket(q.slide_ref).qs.push(q);
    }
    return Array.from(byRef.entries())
      .map(([ref, g]) => [ref, toItems(g.ins, g.qs)] as [string, ContextItem[]])
      .sort((a, b) => slideOrder(a[0]) - slideOrder(b[0]));
  }, [insights, questions, slideOrder, toItems]);

  // The active slide's items pin to the top zone; the rest become the
  // collapsed "Earlier slides" history, most recently left first.
  const onSlideItems = useMemo(
    () => (activeSlideRef && slideGroups.find(([ref]) => ref === activeSlideRef)?.[1]) || [],
    [slideGroups, activeSlideRef]
  );
  const earlierGroups = useMemo(
    () => slideGroups.filter(([ref]) => ref !== activeSlideRef).reverse(),
    [slideGroups, activeSlideRef]
  );

  // The caller's own asked/raised/addressed questions — a persistent tracker
  // so raising a question is never a dead end.
  const myQuestions = useMemo(() => questions.filter((q) => q.status !== 'suggested'), [questions]);

  // Status roll-up for the strip header, e.g. "1 raised · 2 asked · 1 addressed".
  const myQuestionsSummary = useMemo(() => {
    const counts = { raised: 0, asked: 0, addressed: 0 };
    for (const q of myQuestions) {
      if (q.status === 'raised' || q.status === 'asked' || q.status === 'addressed') counts[q.status] += 1;
    }
    return (['raised', 'asked', 'addressed'] as const)
      .filter((s) => counts[s] > 0)
      .map((s) => `${counts[s]} ${s}`)
      .join(' · ');
  }, [myQuestions]);

  // Accent/stale flags for a card. Insights age from created_at; questions
  // (no timestamp on the contract) age from first arrival in this session.
  const cardMeta = (id: string, createdAt?: string) => {
    const stamp = firstSeen.current.get(id) ?? 0;
    const isNew = stamp > 0 && now - stamp < ACCENT_MS;
    const born = createdAt ? Date.parse(createdAt) : NaN;
    const ageMs = !Number.isNaN(born) ? now - born : stamp > 0 ? now - stamp : NaN;
    const stale = !Number.isNaN(ageMs) && ageMs >= STALE_MS;
    return { isNew, stale, ageText: stale ? ageLabel(ageMs) : '' };
  };

  const renderInsightCard = (ins: LiveInsight) => {
    const { isNew, stale, ageText } = cardMeta(ins.id, ins.created_at);
    return (
      <li key={ins.id} className={`insight-card${isNew ? ' card-new' : ''}${stale ? ' card-stale' : ''}`}>
        <span className="card-new-dot" role="img" aria-label="New" aria-hidden={!isNew} />
        <div className="insight-card-head">
          <span className="insight-kicker">Insight</span>
          {stale && <span className="card-stale-badge">{ageText}</span>}
        </div>
        <div className="insight-content">{ins.content}</div>
        {ins.citations.length > 0 && (
          <div className="insight-citations">
            {ins.citations.map((c, idx) => (
              <span
                key={idx}
                className={`insight-citation${c.verified ? ' insight-citation-verified' : ''}`}
                title={`${c.verified ? 'Verified against meeting materials · ' : ''}${c.source}${c.ref ? ` · ${c.ref}` : ''}`}
              >
                {c.verified && (
                  <span className="insight-citation-check" aria-label="Verified source">✓</span>
                )}
                {c.source}{c.ref ? ` · ${c.ref}` : ''}
              </span>
            ))}
          </div>
        )}
      </li>
    );
  };

  const renderQuestionCard = (q: SuggestedQuestion) => {
    // Only grounded questions are answerable from the materials and can be
    // promoted to the Advisor; discussion questions are talking points and offer
    // Raise only. A missing kind is treated as discussion (never promotable).
    const grounded = q.question_kind === 'grounded';
    const { isNew, stale, ageText } = cardMeta(q.id);
    // Only the activated control goes busy; its sibling stays enabled so a
    // slow raise never blocks asking (and vice versa).
    const pending = actPending[q.id];
    const actError = actErrors[q.id];
    return (
      <li
        key={q.id}
        className={`question-card question-${q.status} question-kind-${grounded ? 'grounded' : 'discussion'}${isNew ? ' card-new' : ''}${stale ? ' card-stale' : ''}`}
      >
        <span className="card-new-dot" role="img" aria-label="New" aria-hidden={!isNew} />
        <div className="question-card-head">
          <span className={`question-kind-badge question-kind-badge-${grounded ? 'grounded' : 'discussion'}`}>
            {grounded ? 'Grounded' : 'Discussion'}
          </span>
          {stale && <span className="card-stale-badge">{ageText}</span>}
        </div>
        <div className="question-text">{q.text}</div>
        <div className="question-actions">
          {q.status === 'raised' ? (
            <>
              <span className="question-badge question-badge-raised">Raised to presenter · Waiting</span>
              <button
                className="meeting-btn meeting-btn-ghost meeting-btn-sm"
                disabled={pending === 'lower'}
                onClick={() => runAction(q, 'lower')}
              >
                {pending === 'lower' ? 'Lowering…' : 'Lower hand'}
              </button>
            </>
          ) : q.status === 'addressed' ? (
            <span className="question-badge question-badge-addressed">Addressed by presenter</span>
          ) : q.status === 'asked' ? (
            <span className="question-badge question-badge-asked">Asked</span>
          ) : (
            <>
              {grounded && (
                <button
                  className="meeting-btn meeting-btn-primary meeting-btn-sm"
                  disabled={pending === 'ask'}
                  onClick={() => askFromQuestion(q)}
                >
                  {pending === 'ask' ? 'Opening…' : 'Ask AI Advisor'}
                </button>
              )}
              <button
                className="meeting-btn meeting-btn-ghost meeting-btn-sm"
                title="Shares this question and your name with the presenter."
                disabled={pending === 'raise'}
                onClick={() => runAction(q, 'raise')}
              >
                {pending === 'raise' ? 'Raising…' : '✋ Raise to presenter'}
              </button>
            </>
          )}
        </div>
        {actError && (
          <p className="question-act-error" role="alert">{actError}</p>
        )}
        {q.status === 'suggested' && (
          <p className="question-raise-hint">Raising shares this question and your name with the presenter.</p>
        )}
      </li>
    );
  };

  const renderContextItem = (item: ContextItem) =>
    item.kind === 'insight' ? renderInsightCard(item.insight) : renderQuestionCard(item.question);

  // Short roll-up for a collapsed row, e.g. "3 insights · 2 questions".
  const groupSummary = (items: ContextItem[]): string => {
    const ins = items.filter((i) => i.kind === 'insight').length;
    const qs = items.length - ins;
    const parts: string[] = [];
    if (ins) parts.push(`${ins} insight${ins === 1 ? '' : 's'}`);
    if (qs) parts.push(`${qs} question${qs === 1 ? '' : 's'}`);
    return parts.join(' · ') || 'No items';
  };

  // A collapsed disclosure row (deck-wide, general, or an earlier slide).
  // Native details/summary for built-in accessibility; open state is persisted
  // per key so it survives slide changes for the meeting session.
  const renderCollapsedRow = (
    key: string,
    label: string,
    count: string,
    body: React.ReactNode,
    className = 'assistant-history-group'
  ) => (
    <details
      key={key}
      className={className}
      open={openGroups.has(key)}
      onToggle={(e) => toggleGroup(key, (e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="assistant-history-summary">
        <span className="assistant-history-label">{label}</span>
        <span className="assistant-history-count">{count}</span>
      </summary>
      {body}
    </details>
  );

  const submitPaste = async () => {
    const text = pasteText.trim();
    if (!text || pasting) return;
    setPasting(true);
    try {
      const ok = await onPasteTranscript(text);
      if (ok) setPasteText('');
    } finally {
      setPasting(false);
    }
  };

  const clearTranscript = async () => {
    if (clearing) return;
    if (!window.confirm('Clear the entire transcript? This cannot be undone.')) return;
    setClearing(true);
    try {
      await onClearTranscript();
    } finally {
      setClearing(false);
    }
  };

  // Roving-tabindex arrow-key navigation for the tablist (WAI-ARIA tabs).
  const onTabKeyDown = useCallback((e: React.KeyboardEvent) => {
    const i = TABS.indexOf(tab);
    let next: AssistantTab | null = null;
    if (e.key === 'ArrowRight') next = TABS[(i + 1) % TABS.length];
    else if (e.key === 'ArrowLeft') next = TABS[(i - 1 + TABS.length) % TABS.length];
    else if (e.key === 'Home') next = TABS[0];
    else if (e.key === 'End') next = TABS[TABS.length - 1];
    if (next) {
      e.preventDefault();
      setTab(next);
      tabRefs.current[next]?.focus();
    }
  }, [tab]);

  // The Context count includes every item (pack, general, all slides) so the
  // tab number always matches what the tab actually renders.
  const contextTotal = insights.length + questions.length;
  const tabLabel = (t: AssistantTab): React.ReactNode => {
    if (t === 'transcript') return 'Transcript';
    if (t === 'ask') return 'AI Advisor';
    return (
      <>
        Intelligence{contextTotal ? ` (${contextTotal})` : ''}
        {newContext > 0 && tab !== 'context' && (
          <span className="assistant-tab-unread">{newContext}<span className="sr-only"> new</span></span>
        )}
      </>
    );
  };

  const renderTab = (t: AssistantTab) => (
    <button
      key={t}
      ref={(el) => { tabRefs.current[t] = el; }}
      id={`assistant-tab-${t}`}
      role="tab"
      aria-selected={tab === t}
      aria-controls={`assistant-panel-${t}`}
      tabIndex={tab === t ? 0 : -1}
      className={`assistant-tab${tab === t ? ' active' : ''}`}
      onClick={() => setTab(t)}
      onKeyDown={onTabKeyDown}
    >
      {tabLabel(t)}
    </button>
  );

  return (
    <div className="assistant-panel">
      <div className="assistant-tabs" role="tablist" aria-label="Meeting assistant">
        {TABS.map(renderTab)}
      </div>

      <div className="sr-only" aria-live="polite" role="status">{announcement}</div>

      <div className="assistant-body">
        {tab === 'transcript' && (
          <div id="assistant-panel-transcript" role="tabpanel" aria-labelledby="assistant-tab-transcript" className="assistant-transcript" ref={transcriptScrollRef}>
            {canPaste && segments.length > 0 && (
              <div className="transcript-toolbar">
                <button
                  className="meeting-btn meeting-btn-ghost meeting-btn-sm"
                  onClick={clearTranscript}
                  disabled={clearing}
                >
                  {clearing ? 'Clearing…' : 'Clear transcript'}
                </button>
              </div>
            )}
            {segments.length === 0 && !interimTranscript && (
              <div className="meeting-empty-hint">Transcript will appear here as the meeting proceeds.</div>
            )}
            {segments.length > 0 && (
              <ul className="transcript-list">
                {segments.map((seg) => {
                  const time = formatTime(seg.timestamp);
                  return (
                    <li key={`${seg.sequence}-${seg.id}`} className="transcript-segment">
                      {time && <span className="transcript-time">{time}</span>}
                      {seg.speaker && <span className="transcript-speaker">{seg.speaker}</span>}
                      <span className="transcript-text">{seg.text}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {interimTranscript && (
              <div className="transcript-interim" aria-live="polite">
                {interimTranscript}
              </div>
            )}
            {canPaste && (
              <div className="transcript-paste">
                <label className="sr-only" htmlFor="transcript-paste-input">Paste transcript</label>
                <textarea
                  id="transcript-paste-input"
                  className="transcript-paste-input"
                  placeholder="Paste transcript… (one line per segment, optional 'Speaker: text')"
                  value={pasteText}
                  disabled={pasting}
                  onChange={(e) => setPasteText(e.target.value)}
                />
                <button
                  className="meeting-btn meeting-btn-primary meeting-btn-sm"
                  onClick={submitPaste}
                  disabled={!pasteText.trim() || pasting}
                >
                  {pasting ? 'Adding…' : 'Add'}
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'ask' && (
          <div id="assistant-panel-ask" role="tabpanel" aria-labelledby="assistant-tab-ask" className="assistant-chat">
            <div className="assistant-chat-messages" ref={chatScrollRef}>
              {chatMessages.length === 0 && !chatStreaming && (
                <div className="meeting-empty-hint">
                  Ask about what was just said, a slide, or the materials.
                  <span className="assistant-chat-scope">
                    Answers are personal to you and use only this meeting’s transcript, slides, and materials.
                  </span>
                </div>
              )}
              {chatMessages.map((msg, idx) => {
                const isLastAssistant = chatStreaming && idx === chatMessages.length - 1 && msg.role === 'assistant';
                return (
                  <div key={idx} className={`meeting-chat-msg ${msg.role}`}>
                    {msg.content}
                    {isLastAssistant && !msg.content && (
                      <span className="meeting-generating" style={{ padding: 0 }}>
                        <span className="spinner" /> Thinking…
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="meeting-chat-input-row">
              <label className="sr-only" htmlFor="assistant-chat-input">Ask the advisor</label>
              <input
                id="assistant-chat-input"
                className="meeting-chat-input"
                type="text"
                placeholder="Ask the advisor…"
                value={chatInput}
                disabled={chatStreaming}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitChat(); }}
              />
              <button
                className="meeting-btn meeting-btn-primary"
                onClick={submitChat}
                disabled={!chatInput.trim() || chatStreaming}
              >
                Send
              </button>
            </div>
          </div>
        )}

        {tab === 'context' && (
          <div id="assistant-panel-context" role="tabpanel" aria-labelledby="assistant-tab-context" className="assistant-context-wrap">
            {pendingNew > 0 && (
              <button type="button" className="assistant-jump-pill" onClick={jumpToTop}>
                ↑ {pendingNew} new
              </button>
            )}
            {canRefresh && onRefreshInsights && (
              <div className="assistant-context-toolbar">
                <button
                  type="button"
                  className="meeting-btn meeting-btn-ghost meeting-btn-sm assistant-refresh-btn"
                  onClick={onRefreshInsights}
                  disabled={refreshing}
                  aria-label="Refresh insights and questions"
                  title="Generate fresh insights and questions for the current slide and discussion"
                >
                  {refreshing ? (
                    <>
                      <span className="spinner" /> Refreshing…
                    </>
                  ) : (
                    <>↻ Refresh</>
                  )}
                </button>
              </div>
            )}
            {myQuestions.length > 0 && (
              <div className="assistant-myq-strip">
                {renderCollapsedRow(
                  'context:__myq__',
                  `My questions (${myQuestions.length})`,
                  myQuestionsSummary,
                  <ul className="assistant-list assistant-history-list">{myQuestions.map(renderQuestionCard)}</ul>,
                  'assistant-myq-group'
                )}
              </div>
            )}
            <div className="assistant-context" ref={contextScrollRef} onScroll={onContextScroll}>
              {contextTotal === 0 && contextLoading ? (
                // Initial fetch in flight: static skeletons, not live-announced.
                <div className="assistant-context-loading" aria-hidden="true">
                  <div className="assistant-skeleton-row" />
                  <div className="assistant-skeleton-row" />
                  <p className="assistant-context-preparing">Preparing suggestions…</p>
                </div>
              ) : contextTotal === 0 ? (
                <div className="meeting-empty-hint">
                  {isLive
                    ? 'Insights and suggested questions tailored to you will appear here as the presentation continues.'
                    : 'The session hasn’t started. Insights and suggested questions tailored to you will appear once the presentation is live.'}
                </div>
              ) : (
                <>
                  <section className="assistant-onslide" aria-label="On this slide">
                    <h4 className="assistant-zone-head">
                      On this slide
                      {activeSlideRef && (
                        <span className="assistant-zone-label" title={slideLabel(activeSlideRef)}>
                          {slideLabel(activeSlideRef)}
                        </span>
                      )}
                    </h4>
                    {onSlideItems.length === 0 ? (
                      <p className="assistant-onstage-empty">
                        {/* While a refresh is running the group is empty because
                            we are generating for it, not because nothing is
                            coming. The default copy describes waiting on the
                            presenter, which for a browsing follower is the
                            opposite of what is happening. */}
                        {activeSlideRef && refreshing
                          ? 'Generating insights for this slide…'
                          : activeSlideRef
                            ? 'Nothing for this slide yet. New items appear as the presentation continues.'
                            : 'No slide on stage.'}
                      </p>
                    ) : (
                      <>
                        <ul className="assistant-list">
                          {(openGroups.has('context:__onslide-more__')
                            ? onSlideItems
                            : onSlideItems.slice(0, ON_SLIDE_PREVIEW)
                          ).map(renderContextItem)}
                        </ul>
                        {onSlideItems.length > ON_SLIDE_PREVIEW && (
                          <button
                            type="button"
                            className="assistant-show-more"
                            aria-expanded={openGroups.has('context:__onslide-more__')}
                            onClick={() =>
                              toggleGroup('context:__onslide-more__', !openGroups.has('context:__onslide-more__'))
                            }
                          >
                            {openGroups.has('context:__onslide-more__')
                              ? 'Show less'
                              : `Show ${onSlideItems.length - ON_SLIDE_PREVIEW} more`}
                          </button>
                        )}
                      </>
                    )}
                  </section>

                  {packInsights.length > 0 &&
                    renderCollapsedRow(
                      'context:__pack__',
                      'Deck-wide',
                      `${packInsights.length} insight${packInsights.length === 1 ? '' : 's'}`,
                      <ul className="assistant-list assistant-history-list">{packInsights.map(renderInsightCard)}</ul>,
                      'assistant-wholedeck'
                    )}

                  {generalItems.length > 0 &&
                    renderCollapsedRow(
                      'context:__general__',
                      'General',
                      groupSummary(generalItems),
                      <ul className="assistant-list assistant-history-list">{generalItems.map(renderContextItem)}</ul>
                    )}

                  {earlierGroups.length > 0 && (
                    <div className="assistant-history">
                      <h4 className="assistant-history-head">Earlier slides ({earlierGroups.length})</h4>
                      {earlierGroups.map(([ref, items]) =>
                        renderCollapsedRow(
                          `context:${ref}`,
                          slideLabel(ref),
                          groupSummary(items),
                          <ul className="assistant-list assistant-history-list">{items.map(renderContextItem)}</ul>
                        )
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantPanel;
